/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
// utils
import {
    formatNumber,
    formatXAxisDates,
    getChangeRateDirection,
    getProductDetails,
    reorderProductsData,
    getEnabledRollUps,
    formatTableDate,
    chartTableColors, getChartColorsSubset
} from '../../../../utils/charts';
// material
import { Page, Content, InContentMessage } from '@insticator/insticator-ui';
import Header from '../../../../material/structure/header/header';
import GlobalFilter from '../../../../material/globalfilter/globalfilter';
import PageHeader from '../../../../material/structure/pageheader/pageheader';
import PageContent from '../../../../material/structure/pagecontent/pagecontent';
import CardToggles from '../../../../material/cardwrapper/cardtoggles/cardtoggles';
import CardToggle from '../../../../material/cardwrapper/cardtoggles/cardtoggle/cardtoggle';
import DetailsTable from '../../../../material/detailstable/detailstable';
import BarChart from '../../../../material/barchart/barchart';
import ChartLegend from '../../../../material/chartlegend/chartlegend';
import ChartReportSection from '../../../../material/chartreportsection/chartreportsection';
import MonetizationChartCards from '../chartcards/chartcards';
// styles
import injectSheet from 'react-jss';
import styles from './revenue_styles';



/* ========== ~~~~~~~~~~ MONETIZATION - REVENUE PAGE ~~~~~~~~~~ ========== */
const RevenuePage = injectSheet(styles)((props) => {
    const { overview, details, lastUpdated, status } = useSelector((state) => state.revenue);
    const { filters } = useSelector((state) => state.app);

    const monetizationType = 'revenue';
    const monetizationTypeChangeRate = `${monetizationType}ChangeRate`;

    const changeType = filters.compareTo;

    const [selectedPeriodX, setSelectedPeriodX] = useState(null);
    const [revenueToggle, changeRevenueToggle] = useState('TOTAL');
	const [selectedPeriod, setSelectedPeriod] = useState('daily');
    const dataType = revenueToggle.toUpperCase(); // TOTAL, VIDEO, DISPLAY
    const period = `${selectedPeriod[0].toUpperCase()}${selectedPeriod.slice(1)}`; // Daily, Monthly, Weekly, Quarterly

    const cardtogglesList = [
        {id: 'TOTAL', label: 'Total Revenue'},
        {id: 'VIDEO', label: 'Video'},
        {id: 'DISPLAY', label: 'Display'}
    ];

    const revenueCharts = [
        {id: 'revenue', yType: 'dollar', changeType, title: 'Revenue', route: '/dashboard/monetization/revenue'},
        {id: 'rpm', yType: 'dollar', changeType, title: 'Unit Load RPM', route:'/dashboard/monetization/unitloadrpm'},
        {id: 'unitLoad', yType: '', changeType, title: 'Unit Loads', route: '/dashboard/monetization/unitloads'}
    ];

    const products = reorderProductsData(details[dataType][period]);
    const dates = details['TOTAL'][period]['TOTAL'];
    const currentTotalDetail =  Object.keys(details[dataType][period].TOTAL);

    const [chartLegends, setChartLegends] = useState([]);
    const [showTotal, setShowTotal] = useState(false);
    const [enabledRollups, setEnabledRollups] = useState([]);

    useEffect(() => {
        if(status === 'fulfilled' && chartLegends.length === 0) updateChartLegends();
    }, [products]);

    useEffect(() => {
        if(showTotal) updateChartLegends();
    }, [showTotal]);

    useEffect(() => {
        if(status === 'fulfilled' && enabledRollups.length === 0) {
            const rollups = getEnabledRollUps(details['TOTAL']);
            setEnabledRollups(rollups);
            if(rollups.length > 0)
                setSelectedPeriod(rollups[0])
            else
                setSelectedPeriod('monthly')
        } else {
            setEnabledRollups([]);
            setSelectedPeriod('daily');
            setChartLegends([]);
        }
    }, [status]);

    const computeTableTotal = (products, checkedOnly = true) => {
        // Compute total depending on the selected products
        return products
            .filter(product => product.id !== 'TOTAL' && (checkedOnly ? (product.checked === true) : true))
            .map(product => product.value)
            .reduce((prev, current) => prev + current, 0);
    }

    const updateChartLegends = () => {
        const productArr = Object.keys(products).map(id => ({
            ...getProductDetails(id),
            value: overview['TOTAL'][id][monetizationType],
            changeRate: overview['TOTAL'][id][`${monetizationType}ChangeRate`],
            checked: chartLegends.length > 0 ? chartLegends.filter(legend => legend.id === id)[0].checked : true
        }))

        const totalIndex = productArr.map(product => product.id).indexOf('TOTAL');
        const orderedProducts = [...productArr].filter(product => product.id !== 'TOTAL');
        orderedProducts.sort((a, b) => {
            if(a.value > b.value) return -1;
            if(a.value < b.value) return 1;
            return 0;
        });

        if(totalIndex >= 0 && productArr[totalIndex]) orderedProducts.push({...productArr[totalIndex], value: computeTableTotal(orderedProducts)})

        setChartLegends(orderedProducts);
    }

    const getBarChartKeys = () => [...chartLegends].reverse().map(product => getProductDetails(product.id).name);

    const getBarChartColors = () => getChartColorsSubset(chartLegends.length).reverse()

    const renderBarChartLegends = () => {
        const totalValueIndex = chartLegends.map(product => product.id).indexOf('TOTAL');
        const totalValue = computeTableTotal(chartLegends, false);

        return chartLegends.map((product, index) => {
            const productPercentage = totalValue > 0 ? ((product.value ? product.value : 0) / totalValue) * 100 : 0;
            const productPercentageText = totalValue && (product.value != totalValue) ? '(' + formatNumber(productPercentage, 'percent') + ')' : '';
            if(product.id === 'TOTAL' && chartLegends.length === 2) return null;
            return (
                <ChartLegend
                    title={`${product.name} ${chartLegends.length > 2 && product.id !== 'TOTAL' ? productPercentageText : ''}`}
                    checkboxColor={product.id !== 'TOTAL' ? getBarChartColors()[index] : '#fff'}
                    checked={product.checked}
                    onCheck={checked => {
                        const legends = [...chartLegends];
                        legends[index] = {
                            ...legends[index],
                            checked: checked
                        }

                        legends[totalValueIndex] = {
                            ...legends[totalValueIndex],
                            value: computeTableTotal(legends)
                        }

                        setChartLegends(legends);
                    }}
                    value={formatNumber(product.value, 'dollar')}
                    changeDirection={product.changeRate < 0 ? 'negative' : 'positive'}
                    change={formatNumber(product.changeRate ? product.changeRate : 0, 'percent')}
                    changeType={changeType}
                    metricType={'monetization'}
                />
            );
        })
    }

    const transformBarChartData = () => {
        let barChartData = [];
        barChartData = Object.keys(dates).map(date => {
            const dateLength = Object.keys(dates).length;
            const formattedDate = formatXAxisDates(date, period, dateLength, 10, true);
            let item = {date: formattedDate}
            let total = 0;
            Object.keys(products).forEach(id => {
                const checkedProducts = chartLegends.filter(product => product.checked === true);
                if(id !== 'TOTAL' && checkedProducts.map(product => product.id).indexOf(id) >= 0) {
                    item[getProductDetails(id).name] = products[id] && products[id][date] ? products[id][date][monetizationType] : 0;
                    item[`${getProductDetails(id).name}Color`] = getProductDetails(id).color;
                    item['origDate'] = date;
                    item['formattedTableDate'] = formatTableDate(date, period, products[id][date].startDate, products[id][date].endDate)
                    total += item[getProductDetails(id).name];
                }
                item['Total'] = total;
            })
            return item;
        })
        return barChartData;
    }

    const handleHoverOnBarChart = barData => {
        const date = barData.data ? barData.data.origDate : barData.origDate;
        const legends = [...chartLegends];

        Object.keys(products).forEach(id => {
            let value = 0;
            let changeRate = 0;
            const productDetails = products[id] && products[id][date] ? products[id][date] : null;
            if(productDetails) {
                value = productDetails[monetizationType] ? productDetails[monetizationType] : 0;
                changeRate = productDetails[`${monetizationType}ChangeRate`]  ? productDetails[`${monetizationType}ChangeRate`] : 0;
            }

            const legendIndex = chartLegends.map(product => product.id).indexOf(id);
            legends[legendIndex] = {
                ...legends[legendIndex],
                value,
                changeRate
            }
        })

        // Update total depending on selected products
        const totalValueIndex = chartLegends.map(product => product.id).indexOf('TOTAL');
        legends[totalValueIndex] = {
            ...legends[totalValueIndex],
            value: computeTableTotal(legends)
        }

        setChartLegends(legends);
        setSelectedPeriodX(`${barData.data ? barData.data.formattedTableDate : barData.formattedTableDate}`);
        setShowTotal(false);
    }

    const handleMouseLeave = () => {
        setShowTotal(true);
        setSelectedPeriodX('');
    }


    const monetizationBarChart = {
        indexBy: 'date',
        data: transformBarChartData(),
        keys: getBarChartKeys(),
        legends: renderBarChartLegends()
    }

    const cardtoggles = (
		<CardToggles>
            { cardtogglesList.filter(card => overview[card.id].revenue > 0).map(card =>
                card.id !== 'TOTAL' && overview[card.id].revenue === overview.TOTAL.revenue ? null : (
                    <CardToggle
                        label={card.label}
                        compareType={changeType}
                        metricType={'monetization'}
                        handleClick={() => {
                            changeRevenueToggle(card.id);
                            setChartLegends([]);
                            handleMouseLeave();
                        }}
                        percentChange={{
                            value: formatNumber(overview[card.id][monetizationTypeChangeRate], 'percent'),
                            direction: getChangeRateDirection(overview[card.id][monetizationTypeChangeRate])
                        }}
                        selected={revenueToggle === card.id}
                        value={formatNumber(overview[card.id].revenue, 'dollar')}
                    />
                )
            )}
		</CardToggles>
	);

    const barchart = (
        <BarChart
            indexBy={'date'}
            colors={monetizationBarChart.colors}
            keys={monetizationBarChart.keys}
            data={monetizationBarChart.data}
            period={selectedPeriod}
            metricType={'dollar'}
            onHover={handleHoverOnBarChart}
            onMouseLeave={handleMouseLeave}
            reset={showTotal}
            yType={'dollar'}
        />
    );

    const detailtable = (
        <DetailsTable
            tableType={'monetization'}
            dataType={monetizationType} // revenue, unitLoad, rpm
            adsType={dataType} // TOTAL, DISPLAY, VIDEO
            period={period} // Daily, Weekly, Monthly, Quarterly
            data={details}
            csvFileName={'Revenue'}
        />
    );

    const isNoData = () => {
        return  (status === 'rejected') ||
                (status === 'fulfilled' && currentTotalDetail.length === 0) ||
                (status === 'fulfilled' && overview[dataType].revenue === 0);
    }



    return (
        <Page name={'revenue'} klass={`${props.classes.revenue}`} >
            <Header>
                <GlobalFilter onFilterApply={props.dataRequest} />
            </Header>
            <Content>
                <InContentMessage/>
                <PageHeader lastUpdatedTimestamp={lastUpdated} title={'Monetization'} subtitle={'Revenue'} dataTip={true} />
                <PageContent>
                    <MonetizationChartCards charts={revenueCharts} />
                    <ChartReportSection
                        legendTitle={selectedPeriodX}
                        title={'Total Revenue'}
                        tooltipContent={
                            <div className={'tip-content'}>
                                <div className={'tip-title'}>
                                    <h4>Revenue</h4>
                                </div>
                                <div className={'tip-desc'}>
                                    <p>Shown revenue is your total net ad revenue, for the selected time period.
                                        Net revenue is your portion of revenue after the negotiated
                                        Insticator portion of the revenue share.</p>
                                    <p>Factors that can influence revenue are:</p>
                                    <ul>
                                        <li>Seasonality</li>
                                        <li>CPM</li>
                                        <li>Site traffic</li>
                                        <li>Ad size and placement</li>
                                        <li>Display vs. video ads</li>
                                        <li>Ad fill rates</li>
                                        <li>Viewability of the ad</li>
                                        <li>Refresh rates</li>
                                    </ul>
                                </div>
                            </div>
                        }
                        cardtoggles={cardtoggles}
                        chart={barchart}
                        loading={status === 'pending' ? true : false}
                        noData={isNoData()}
                        enabledPeriods={enabledRollups}
                        chartLegend={monetizationBarChart.legends}
                        handleSelectPeriod={periodData => {
                            setSelectedPeriod(periodData);
                            handleMouseLeave();
                        }}
                        selectedPeriod={selectedPeriod}
                        detailTable={detailtable}
                    />
                </PageContent>
            </Content>
        </Page>
    );
});


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default RevenuePage;
